import { Text } from '@nerdwallet/currency';
import Lottie from 'lottie-react';
import { useMemo } from 'react';
import { animationModes, interactivity, isJsonLottieString } from './lib';
import { LottieIcon } from './lib/lottie-icon';

function renderAnimation(
  animationMode: string,
  animationData: unknown,
  visibility: { x: string; y: string },
  frames: { x: string; y: string },
  dimensions?: {
    width?: string | number;
    height?: string | number;
  }
) {
  return (
    <Lottie
      width={dimensions?.width}
      height={dimensions?.height}
      autoPlay
      interactivity={interactivity(animationMode, visibility, frames) as any}
      animationData={animationData}
    />
  );
}

type BaseLottieAnimationProps = {
  lottiePlayerId: string;
  animationData: string;
  animationMode: string;
  visibility: { x: string; y: string };
  frames: { x: string; y: string };
  dimensions?: { width?: string | number; height?: string | number };
};

const BaseLottieAnimation = ({
  lottiePlayerId,
  animationData,
  animationMode,
  visibility,
  frames,
  dimensions,
}: BaseLottieAnimationProps) => {
  // Because of page re-renders AnimationData reference is lost and can lead into errors.
  // To prevent this we memoize it
  const validAnimationData = useMemo(
    () => isJsonLottieString(animationData),
    [animationData]
  );

  const showAnimation = animationData && validAnimationData;
  const showInvalidJsonMessage = animationData && !validAnimationData;

  const visibilityObj = useMemo(
    () => ({ x: visibility.x || '0.5', y: visibility.y || '1.0' }),
    [visibility.x, visibility.y]
  );

  const framesObj = useMemo(
    () => ({ x: frames.x || '0.5', y: frames.y || '1.0' }),
    [frames.x, frames.y]
  );

  const onScroll = useMemo(
    () =>
      renderAnimation(
        animationModes.SEEK,
        validAnimationData,
        visibilityObj,
        framesObj
      ),
    [framesObj, validAnimationData, visibilityObj]
  );

  const whenVisible = useMemo(
    () =>
      renderAnimation(
        animationModes.PLAY,
        validAnimationData,
        visibilityObj,
        framesObj
      ),
    [framesObj, validAnimationData, visibilityObj]
  );
  let animation = null;
  if (animationMode === animationModes.PLAY) {
    animation = whenVisible;
  } else if (animationMode === animationModes.SEEK) {
    animation = onScroll;
  }

  return (
    <div
      id={lottiePlayerId}
      data-testid={lottiePlayerId}
      style={{
        width: dimensions?.width || 300,
        height: dimensions?.height || 300,
      }}
    >
      {!animationData && <LottieIcon />}
      {showInvalidJsonMessage && <Text>Invalid JSON value!</Text>}
      {/*
         Currently we are not able to get different output of Lottie by only
         changing its properties, we have to create different instances with
         its own settings then output it as we see below
       */}
      {showAnimation && animation}
    </div>
  );
};

export default BaseLottieAnimation;
